/**
 * Enrutador SPA de la Aplicación
 * Administra el cambio de pantallas, persistencia de sesión y autorización de roles.
 * Cumple con .idx-rules.md: funciones desacopladas, archivos < 300 líneas.
 */

import { getUserInfo } from '../infra/db.js';

const SESSION_KEY = 'dni_reader_session';
const ONE_DAY_MS = 24 * 60 * 60 * 1000;

// Estado del usuario activo (persistencia con localStorage)
let currentUser = null;
try {
  const cached = localStorage.getItem(SESSION_KEY);
  if (cached) {
    const sessionData = JSON.parse(cached);
    if (sessionData && sessionData.loginTimestamp && (Date.now() - sessionData.loginTimestamp < ONE_DAY_MS)) {
      currentUser = sessionData.user;
    } else {
      localStorage.removeItem(SESSION_KEY);
    }
  }
} catch (err) {
  console.error('Error al restaurar sesión:', err);
}

export function getCurrentUser() {
  return currentUser;
}

export function setCurrentUser(user) {
  currentUser = user;
  try {
    if (user) {
      localStorage.setItem(SESSION_KEY, JSON.stringify({
        user,
        loginTimestamp: Date.now()
      }));
    } else {
      localStorage.removeItem(SESSION_KEY);
    }
  } catch (err) {
    console.error('Error al guardar sesión:', err);
  }
}

/**
 * Navega a una pantalla específica, verificando permisos y sincronizando hash.
 * @param {string} viewId ID de la vista ('login', 'scan', 'reports', 'config')
 * @param {boolean} updateHash Si debe actualizarse el hash en la URL
 */
export function navigateTo(viewId, updateHash = true) {
  // Redireccionar si ya hay sesión activa y se intenta ir a login
  if (currentUser && viewId === 'login') {
    viewId = 'scan';
  }

  // Verificación de autenticación
  if (!currentUser && viewId !== 'login') {
    return navigateTo('login');
  }

  // Sincronizar y validar sesión en segundo plano si hay un usuario logueado
  if (currentUser && viewId !== 'login') {
    checkAndSyncUserSession(currentUser.username).catch(() => { });
  }

  // Verificación de rol para vistas restringidas
  if (currentUser) {
    if (viewId === 'users' && currentUser.role !== 'superadmin' && currentUser.role !== 'admin') {
      alert('Acceso denegado: Se requieren privilegios de Administrador.');
      return navigateTo('scan');
    }
    const adminViews = ['reports', 'config', 'assistants'];
    if (adminViews.includes(viewId) && currentUser.role !== 'admin' && currentUser.role !== 'superadmin') {
      alert('Acceso denegado: Se requieren privilegios de Administrador.');
      return navigateTo('scan');
    }
  }

  // Ocultar todas las pantallas y mostrar la seleccionada
  const views = document.querySelectorAll('.app-view');
  views.forEach(v => v.classList.remove('active'));

  const activeView = document.getElementById(`view-${viewId}`);
  if (activeView) {
    activeView.classList.add('active');
  }

  // Sincronizar botones de navegación activos
  updateActiveNavState(viewId);

  // Controlar visibilidad de los paneles de navegación
  toggleNavContainers();

  // Disparar eventos de ciclo de vida
  handleViewLifecycle(viewId);

  // Actualizar hash en la URL
  if (updateHash) {
    window.location.hash = viewId;
  }
}

/**
 * Sincroniza la clase active de todos los controles de navegación.
 */
function updateActiveNavState(viewId) {
  document.querySelectorAll('[data-target]').forEach(button => {
    const target = button.getAttribute('data-target');
    if (target === viewId) {
      button.classList.add('active');
    } else {
      button.classList.remove('active');
    }
  });
}

/**
 * Muestra u oculta la barra de navegación lateral y toggle según la sesión.
 */
function toggleNavContainers() {
  const isLogged = currentUser !== null;
  const role = currentUser ? currentUser.role : null;

  // Panel de menú lateral (sidebar)
  const sidebar = document.getElementById('app-sidebar');
  if (sidebar) {
    sidebar.style.display = isLogged ? 'flex' : 'none';
  }

  // Botón hamburguesa de cabecera
  const sidebarToggle = document.getElementById('btn-sidebar-toggle');
  if (sidebarToggle) {
    sidebarToggle.style.display = isLogged ? 'inline-flex' : 'none';
  }

  // Badge de aforo en la cabecera
  const headerCapacity = document.getElementById('header-capacity-badge');
  if (headerCapacity) {
    headerCapacity.style.display = isLogged ? 'inline-flex' : 'none';
  }

  // Controlar visibilidad de los elementos del menú según roles
  if (isLogged) {
    document.querySelectorAll('.admin-only').forEach(el => {
      el.style.display = (role === 'admin' || role === 'superadmin') ? 'flex' : 'none';
    });
    // Visible para todos los superadmins (global o de empresa)
    document.querySelectorAll('.superadmin-only').forEach(el => {
      el.style.display = role === 'superadmin' ? 'flex' : 'none';
    });
  }

  // Info del usuario activo en el sidebar
  const userInfo = document.getElementById('sidebar-user-info');
  const userName = document.getElementById('sidebar-user-name');
  if (userInfo && userName) {
    if (isLogged && currentUser.fullName) {
      userName.innerText = currentUser.fullName;
      userInfo.style.display = 'flex';
    } else {
      userName.innerText = '';
      userInfo.style.display = 'none';
    }
  }
}

/**
 * Ejecuta acciones al cargar cada vista (por ejemplo, encender/apagar cámara, refrescar reportes).
 */
function handleViewLifecycle(viewId) {
  // Apagar la cámara al salir de la pantalla de escaneo
  if (viewId !== 'scan') {
    window.dispatchEvent(new Event('stop_camera_scan'));
  } else {
    window.dispatchEvent(new Event('start_camera_scan'));
  }

  // Inicializar o refrescar reportes
  if (viewId === 'reports') {
    window.dispatchEvent(new Event('load_reports'));
  }

  // Cargar lista de asistentes
  if (viewId === 'assistants') {
    window.dispatchEvent(new Event('load_assistants'));
  }

  // Cargar lista negra y configs
  if (viewId === 'config') {
    window.dispatchEvent(new Event('load_config_settings'));
  }

  // Cargar listado de usuarios (CRUD)
  if (viewId === 'users') {
    window.dispatchEvent(new Event('load_users'));
  }
}

/**
 * Inicializa los listeners de navegación, cambios de hash y toggles de menús.
 */
export function initNavigation() {
  // Eventos de botones con data-target
  document.querySelectorAll('[data-target]').forEach(button => {
    button.addEventListener('click', (e) => {
      const target = e.currentTarget.getAttribute('data-target');
      navigateTo(target);
    });
  });

  // Evento de botones de cierre de sesión
  const logoutBtns = document.querySelectorAll('.btn-logout-action');
  logoutBtns.forEach(btn => {
    btn.addEventListener('click', () => {
      setCurrentUser(null);
      navigateTo('login');
    });
  });

  // Evento de botón de soporte
  const supportBtn = document.getElementById('btn-support');
  if (supportBtn) {
    supportBtn.addEventListener('click', () => {
      alert('💬 Soporte técnico: Contacte al 994 595 104. MAITI PERU');
    });
  }

  // Listener reactivo a cambios del hash (para recargas y botones de navegación del browser)
  window.addEventListener('hashchange', () => {
    const hash = window.location.hash.substring(1);
    if (hash) {
      navigateTo(hash, false);
    }
  });

  // Toggle del menú lateral en móviles (Drawer)
  const sidebarToggle = document.getElementById('btn-sidebar-toggle');
  const sidebar = document.getElementById('app-sidebar');
  if (sidebarToggle && sidebar) {
    sidebarToggle.addEventListener('click', (e) => {
      e.stopPropagation();
      sidebar.classList.toggle('sidebar-open');
    });

    // Cerrar al hacer clic fuera del sidebar
    document.addEventListener('click', (e) => {
      if (sidebar.classList.contains('sidebar-open') && !sidebar.contains(e.target) && e.target !== sidebarToggle) {
        sidebar.classList.remove('sidebar-open');
      }
    });

    // Cerrar al hacer clic en cualquier enlace del menú
    sidebar.querySelectorAll('[data-target]').forEach(el => {
      el.addEventListener('click', () => {
        sidebar.classList.remove('sidebar-open');
      });
    });
  }

  // Determinar la pantalla inicial al arrancar
  const initialHash = window.location.hash.substring(1) || 'scan';
  navigateTo(currentUser ? initialHash : 'login');
}

/**
 * Verifica asíncronamente si el usuario actual sigue activo y actualiza sus datos de sesión.
 */
async function checkAndSyncUserSession(username) {
  try {
    const dbUser = await getUserInfo(username);
    if (!dbUser || dbUser.status !== 'ACTIVE') {
      alert('Tu sesión ha expirado o tu usuario ha sido desactivado.');
      setCurrentUser(null);
      navigateTo('login');
      return;
    }

    // Si cambió el rol o el nombre completo o la empresa, actualizar sesión local y refrescar la barra lateral
    if (dbUser.role !== currentUser.role || dbUser.fullName !== currentUser.fullName || dbUser.company_id !== currentUser.company_id) {
      currentUser.role = dbUser.role;
      currentUser.fullName = dbUser.fullName;
      currentUser.company_id = dbUser.company_id;
      setCurrentUser(currentUser);
      toggleNavContainers();
    }
  } catch (err) {
    console.warn('No se pudo verificar la sesión con la BD:', err);
  }
}
